import React, { useState } from "react";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillNo from "..";
import ShopType from "@/enum/ShopType";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import { observer } from "mobx-react-lite";
import RetailerEwaybill from "./RetailerEwaybill";
import OrderStatus from "@/enum/OrderStatus";

function buildEwaybillNo(
    order: OrderDTO,
    ewaybill: EwaybillDTO,
    hasMultiEwaybillNos: boolean,
    handleUpdateMainEwaybillSuccess?: (order: OrderDTO) => void,
) {
    const waybillNo = ewaybill.waybillNo;

    if (!waybillNo) {
        return null;
    }

    const isMainWaybillNo = order.waybillNo === waybillNo || order.waybillNo === ewaybill.parentWaybillNo;

    return (
        <WaybillNo
            key={waybillNo}
            ewaybill={ewaybill}
            hasEwaybillNo
            isMainWaybillNo={isMainWaybillNo}
            hasMultiEwaybillNos={hasMultiEwaybillNos}
            order={order}
            onUpdateMainEwaybillSuccess={handleUpdateMainEwaybillSuccess}
        />
    );
}

interface EwaybillNoComponentsProps {
    order: OrderDTO;

    handleUpdateMainEwaybillSuccess?: (order: OrderDTO) => void;

    shopType?: ShopType;
}

const EwaybillNoComponents: React.FC<EwaybillNoComponentsProps> = function (props) {
    const { order, handleUpdateMainEwaybillSuccess, shopType } = props;

    // 如果电子面单号超过5个，默认只显示前5个，点击『显示所有运单号时』再显示其余的
    const [isShowAllEwaybillNos, setShowAllEwaybillNos] = useState(false);

    /**
     * 显示电子面单号,
     * 1、排序：主运单号排在第一个，多个运单号按打印时间倒序排列(排序在service/api/order.js中实现)
     * 2、如果单号超过5个，默认只显示前5个，当用户点击『显示所有单号』时才会显示其余的出来
     * 3、代理商预约的单号排在后面
     */
    let ewaybills = order?.ewaybills;
    let supplierEwaybills = order?.retailerDropShippingInfo?.packages;

    const ewaybillCount = ewaybills?.length || 0;
    const supplierWaybillCount = supplierEwaybills?.length || 0;
    /**总数 */
    const count = ewaybillCount + supplierWaybillCount;

    const hasMultiEwaybills = count > 1;

    if (count > 5 && isShowAllEwaybillNos === false) {
        const components: React.ReactNode[] = [];

        for (let i = 0; i < 5; i++) {
            // 渲染代理商预约的单号
            if (i > ewaybillCount - 1) {
                const packageDto = supplierEwaybills?.[i - ewaybillCount];

                components.push(<RetailerEwaybill packageDto={packageDto} key={i} />);
            }
            // 渲染自己的电子面单
            else {
                const ewaybill = ewaybills[i];

                const component = buildEwaybillNo(order, ewaybill, hasMultiEwaybills, handleUpdateMainEwaybillSuccess);

                components.push(component);
            }
        }

        components.push(
            <a
                key="all"
                href="#"
                onClick={(e) => {
                    e.preventDefault();

                    setShowAllEwaybillNos(true);
                }}
            >
                <strong>显示所有单号({ewaybillCount || supplierWaybillCount})</strong>
            </a>,
        );

        return <>{components}</>;
    }

    const showMultiplePackageTag =
        shopType === ShopType.PINDUODUO &&
        hasMultiEwaybills &&
        order.mergeSplitStatus === OrderMergeSplitStatus.INDEPENDENT &&
        order.status === OrderStatus.PENDING_SHIPMENT;

    return (
        <>
            {showMultiplePackageTag && <p className="error waybill-no-separator">订单发货时全部运单号都会回传</p>}

            {ewaybills?.map((ewaybill) =>
                buildEwaybillNo(order, ewaybill, hasMultiEwaybills, handleUpdateMainEwaybillSuccess),
            )}

            {supplierEwaybills?.map((packageDto, index) => {
                return <RetailerEwaybill packageDto={packageDto} key={index} />;
            })}
        </>
    );
};

export default observer(EwaybillNoComponents);
